/**
 * Contractor scope for profit-center users acting on behalf of their
 * contractors.
 *
 * Mounted app-wide (inactive for contractor orgs — usePcOrderScope returns
 * null) so the scope survives navigation between the contractor-scoped
 * authoring pages: the PC new-order flow, the photo/list flow (/list), and
 * order editing (/orders/edit/:id).
 *
 * Provides:
 * - The list of contractors affiliated with the PC (GET /api/pc/contractors).
 * - The currently selected contractor, sticky per user (localStorage), so
 *   returning staff land straight in their last contractor's personalized flow.
 * - Request scoping: selection is mirrored synchronously into the module-level
 *   contractor scope (see lib/contractor-scope) that every fetch layer reads
 *   to attach the x-contractor-org-id header.
 *
 * Cache isolation lives in the separate ContractorScopedCache wrapper, applied
 * only around authoring routes so dashboard/management pages keep the root
 * query cache.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { createAppQueryClient } from "@/lib/queryClient";
import { setActiveContractorScope } from "@/lib/contractor-scope";
import { useAuthenticatedQuery } from "@/hooks/use-api";
import { useOrgMetadata } from "@/hooks/use-org-metadata";
import type { PcContractorSummary } from "@onsemble/shared/types/pc-contractors";

export type PcContractor = PcContractorSummary;

interface PcOrderScope {
  /** Contractors affiliated with this profit center. */
  contractors: PcContractor[];
  contractorsLoading: boolean;
  contractorsError: Error | null;
  /** Currently selected contractor, or null in browse-first mode. */
  contractor: PcContractor | null;
  selectContractor: (contractor: PcContractor | null) => void;
  /** True once the sticky last-contractor restore has been attempted. */
  restored: boolean;
}

const PcOrderScopeContext = createContext<PcOrderScope | null>(null);

/** Null for contractor users (the provider is inactive outside PC orgs). */
export function usePcOrderScope(): PcOrderScope | null {
  return useContext(PcOrderScopeContext);
}

const stickyKey = (userId: string) => `pc-ordering-for:${userId}`;

function readStickyContractorId(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(stickyKey(userId));
  } catch {
    return null;
  }
}

function writeStickyContractorId(
  userId: string | undefined,
  orgId: string | null,
) {
  if (!userId) return;
  try {
    if (orgId) localStorage.setItem(stickyKey(userId), orgId);
    else localStorage.removeItem(stickyKey(userId));
  } catch {
    // Sticky selection is a convenience; ignore storage failures
  }
}

export function PcOrderScopeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useUser();
  const { metadata } = useOrgMetadata();
  const isProfitCenter = metadata?.type === "profit-center";

  const [contractor, setContractor] = useState<PcContractor | null>(null);
  const [restored, setRestored] = useState(false);

  const {
    data: contractors,
    isLoading: contractorsLoading,
    error: contractorsError,
  } = useAuthenticatedQuery<PcContractor[]>(["/api/pc/contractors"], {
    enabled: isProfitCenter,
  });

  const selectContractor = useCallback(
    (next: PcContractor | null) => {
      // Synchronous: requests fired by the re-render must already carry the header
      setActiveContractorScope(next?.orgId ?? null);
      writeStickyContractorId(user?.id, next?.orgId ?? null);
      setContractor(next);
    },
    [user?.id],
  );

  // Bind a contractor as soon as the list arrives: the user's sticky last
  // contractor, or the first affiliated contractor as the default. There is
  // deliberately no unbound state — a PC user is always acting for exactly
  // one contractor (they can switch via the picker), so every downstream
  // page works with a single code path. Unbound only remains possible when
  // the PC has no contractors at all.
  useEffect(() => {
    if (!isProfitCenter || restored || !contractors) return;
    setRestored(true);
    const stickyId = readStickyContractorId(user?.id);
    const initial =
      (stickyId && contractors.find((c) => c.orgId === stickyId)) ||
      contractors[0];
    if (initial) {
      setActiveContractorScope(initial.orgId);
      setContractor(initial);
    }
  }, [isProfitCenter, contractors, restored, user?.id]);

  // Leaving PC context (org switch, logout) must always clear the request scope
  useEffect(() => {
    if (!isProfitCenter) setActiveContractorScope(null);
    return () => setActiveContractorScope(null);
  }, [isProfitCenter]);

  if (!isProfitCenter) {
    return (
      <PcOrderScopeContext.Provider value={null}>
        {children}
      </PcOrderScopeContext.Provider>
    );
  }

  const scope: PcOrderScope = {
    contractors: contractors ?? [],
    contractorsLoading,
    contractorsError: (contractorsError as Error) ?? null,
    contractor,
    selectContractor,
    restored,
  };

  return (
    <PcOrderScopeContext.Provider value={scope}>
      {children}
    </PcOrderScopeContext.Provider>
  );
}

/**
 * Per-contractor React Query cache. Wrap contractor-scoped authoring pages
 * (order flow, list flow, order editing) so one contractor's cached carts,
 * pricing, and history can never bleed into another's. Clients are kept per
 * contractor for the component's lifetime, so switching back is instant.
 * Pass-through when no contractor is bound (or for contractor users).
 */
export function ContractorScopedCache({
  children,
}: {
  children: React.ReactNode;
}) {
  const scope = usePcOrderScope();
  const clientsRef = useRef(new Map<string, QueryClient>());

  if (!scope?.contractor) return <>{children}</>;

  let client = clientsRef.current.get(scope.contractor.orgId);
  if (!client) {
    client = createAppQueryClient();
    clientsRef.current.set(scope.contractor.orgId, client);
  }
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
